import { useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { MapPin } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import { type QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { LoadingButton } from '@modules/_shared/components/LoadingButton';
import { PlacePicker } from '@modules/_shared/components/place-picker/PlacePicker';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { useLocale } from '@shared/hooks/locale';

const GET_PLACES_WITH_PARENTS = gql(/* GraphQL */ `
  query GetPlacesWithParents($ids: [uuid!]!) {
    places(where: { id: { _in: $ids } }) {
      id
      parent_places {
        id
      }
    }
  }
`);

type PlaceWhereClause = {
  id?: { _in?: string[] };
  _or?: {
    id?: { _in?: string[] };
    parent_places?: { id?: { _in?: string[] } };
  }[];
};

// Reads back only the user-selected place IDs from the stored where clause,
// ignoring any ancestor IDs that may have been expanded into the query.
export const extractPlacesSelectedIds = (
  parentValue: PlaceWhereClause,
  includeParentsPlaces: boolean,
  expandSelectionAncestors: boolean,
): string[] => {
  if (expandSelectionAncestors) {
    // New format (with ancestor expansion): user-selected IDs live in
    // _or[1].parent_places.id._in when combined with includeParentsPlaces,
    // otherwise we fall back to _or[0].id._in (old format) or direct id._in.
    return (
      parentValue?._or?.find(c => c.parent_places != null)?.parent_places?.id
        ?._in ??
      parentValue?._or?.find(c => c.id != null)?.id?._in ??
      parentValue?.id?._in ??
      []
    );
  }

  if (includeParentsPlaces) {
    return parentValue?._or?.find(c => c.id != null)?.id?._in ?? [];
  }

  return parentValue?.id?._in ?? [];
};

export const PlaceFilter = ({
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  getValueFromPath,
  queryParamsScope,
  disabled,
  includeParentsPlaces = false,
  expandSelectionAncestors = false,
}: QuickFilterProps) => {
  const { t } = useLocale();
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const [getPlacesWithParents] = useLazyQuery(GET_PLACES_WITH_PARENTS);
  const parentValue = getValueFromPath(path, where);
  const defaultValue = extractPlacesSelectedIds(
    parentValue,
    includeParentsPlaces,
    expandSelectionAncestors,
  );
  const [value, setValue] = useState<string[]>(defaultValue);
  const [error, setError] = useState<boolean>(false);

  const onChange = async (selectedIds: string[]) => {
    let newWhere = deleteWhereClause(where, path, true);
    if (selectedIds.length > 0) {
      if (expandSelectionAncestors) {
        // Fetch each selected place's parent_places and merge them into the
        // id._in branch so the query matches entities whose single place column
        // is the selection itself or any of its ancestors (locality -> municipality
        // -> district -> state -> ...). The parent_places branch (when
        // includeParentsPlaces is also on) keeps only the user-selected IDs so we
        // still match candidates that are descendants of the selection.
        const { data } = await getPlacesWithParents({
          variables: { ids: selectedIds },
        });
        const ancestorIds =
          data?.places.flatMap(p => p.parent_places?.map(pp => pp.id) ?? []) ??
          [];
        const expandedIds = [...new Set([...selectedIds, ...ancestorIds])];
        newWhere = addWhereClause(
          newWhere,
          path,
          includeParentsPlaces
            ? {
                _or: [
                  { id: { _in: expandedIds } },
                  { parent_places: { id: { _in: selectedIds } } },
                ],
              }
            : { id: { _in: expandedIds } },
        );
      } else if (includeParentsPlaces) {
        newWhere = addWhereClause(newWhere, path, {
          _or: [
            { id: { _in: selectedIds } },
            { parent_places: { id: { _in: selectedIds } } },
          ],
        });
      } else {
        newWhere = addWhereClause(
          newWhere,
          [...path, 'id', '_in'],
          selectedIds,
        );
      }
    }
    setFiltersParams(newWhere);
  };

  const displayLabel =
    defaultValue.length > 0 ? `${label} (${defaultValue.length})` : label;
  return (
    <>
      <FilterChip
        label={displayLabel}
        icon={<MapPin size={16} />}
        onDelete={defaultValue.length > 0 ? () => onChange([]) : undefined}
        disabled={disabled?.(where) ?? false}
        renderFilter={({ handleClose }) => (
          <Dialog
            open
            onClose={handleClose}
            fullWidth
            maxWidth="xl"
            slotProps={{
              paper: {
                sx: {
                  height: '100%',
                  borderRadius: '10px',
                },
              },
            }}
          >
            <DialogTitle
              sx={{ fontSize: '1.125rem', fontWeight: 600, py: 2, px: 3 }}
            >
              {label}
            </DialogTitle>
            <DialogContent
              sx={{
                pb: 0,
                px: 3,
              }}
            >
              <PlacePicker defaultValue={defaultValue} onChange={setValue} />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  borderColor: 'grey.300',
                  color: 'text.primary',
                }}
              >
                {t('Cancel')}
              </Button>
              <LoadingButton
                onClick={async () => {
                  await onChange(value).catch(() => {
                    setError(true);
                    setValue(defaultValue);
                  });
                  handleClose();
                }}
                variant="contained"
                disableElevation
              >
                {t('Apply')}
              </LoadingButton>
            </DialogActions>
          </Dialog>
        )}
      />
      {error && (
        <RASnackbar
          onClose={() => setError(false)}
          message={t('Error applying filter')}
          severity="error"
        />
      )}
    </>
  );
};
